import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    computed,
    effect,
    ElementRef,
    HostListener,
    input,
    OnDestroy,
    output,
    ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ArrowUp, MessageCirclePlus } from 'lucide-angular';
import MarkdownIt from 'markdown-it';
import { SkeletonModule } from 'primeng/skeleton';
import { baseKeymap, setBlockType, toggleMark } from 'prosemirror-commands';
import { history, redo, undo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import {
    defaultMarkdownSerializer,
    MarkdownParser,
    MarkdownSerializer,
    schema,
} from 'prosemirror-markdown';
import { Schema } from 'prosemirror-model';
import { EditorState, Plugin, PluginKey } from 'prosemirror-state';
import { addRowAfter, deleteRow, goToNextCell, isInTable, tableEditing, tableNodes } from 'prosemirror-tables';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { InputComponent } from 'src/app/@core/components/ui/input/input.component';
import { ToastService } from 'src/app/@core/services/toast.service';
import { GENERAL_AGENT_SVG } from 'src/app/common/svgs';
import { MessageItem } from '../../../models/model'; // Updated import
import { GeneralAgentService } from '../../../services/general-agent.service'; // Updated import
import { ThreadMessagesService } from '../../../services/thread-messages.service'; // Updated import

@Component({
    selector: 'app-canvas',
    imports: [CommonModule, IconComponent, ButtonComponent, InputComponent, FormsModule, SkeletonModule],
    templateUrl: './canvas.component.html',
    styleUrl: './canvas.component.scss',
})
export class CanvasComponent implements AfterViewInit, OnDestroy {
    @ViewChild('editorContainer', { static: false })
    editorContainer!: ElementRef<HTMLDivElement>;
    @ViewChild('parentContainer', { static: false })
    parentContainer!: ElementRef<HTMLDivElement>;

    @ViewChild('toolbar', { static: false })
    toolbarElement?: ElementRef<HTMLElement>;

    @ViewChild('headingsPanel', { static: false })
    headingsPanel?: ElementRef<HTMLElement>;

    @ViewChild('bobInput', { static: false })
    bobInput?: InputComponent;

    message = input<MessageItem | null>(null);
    threadMetadataId = input<string>();
    isEditable = input<boolean>(true);
    showLoading = input<boolean>(false);

    editCanvasWithAi = output<{ query: string, selectedCanvasText: string, canvasName: string }>();

    canvasContent = computed(() => {
        if (!this.message()) return '';
        const message = this.message() as MessageItem;

        // ✅ Priority 1: Check for subagent structured_result (from subagent)
        if (message.additional_kwargs?.structured_result) {
            const result = message.additional_kwargs.structured_result;
            // Try multiple possible field names
            const content = result.content || result.canvasContent || result.canvas_content || '';
            console.log('[Canvas] Subagent structured_result content:', content.substring(0, 100)); // Debug
            return content;
        }

        // ✅ Priority 2: Check for regular canvas
        if (message.additional_kwargs?.canvasContent) {
            return message.additional_kwargs.canvasContent;
        }
        return '';
    });

    editorView: EditorView | null = null;
    private mySchema = new Schema({
        nodes: (schema.spec.nodes as any).append(tableNodes({ tableGroup: 'block', cellContent: 'inline*', cellAttributes: {} })),
        marks: schema.spec.marks,
    });

    // Custom parser with table support (uses default markdown-it preset which includes GFM tables,
    // unlike prosemirror-markdown's defaultMarkdownParser which uses the commonmark preset)
    private mdParser = new MarkdownParser(this.mySchema, new MarkdownIt('default', { html: false, linkify: true }), {
        blockquote: { block: 'blockquote' },
        paragraph: { block: 'paragraph' },
        list_item: { block: 'list_item' },
        bullet_list: { block: 'bullet_list' },
        ordered_list: { block: 'ordered_list', getAttrs: (e: any) => ({ order: +e.attrGet('start') || 1 }) },
        heading: { block: 'heading', getAttrs: (e: any) => ({ level: +e.tag.slice(1) }) },
        code_block: { block: 'code_block', noCloseToken: true },
        fence: { block: 'code_block', getAttrs: (e: any) => ({ params: e.info || '' }), noCloseToken: true },
        hr: { node: 'horizontal_rule' },
        image: { node: 'image', getAttrs: (e: any) => ({ src: e.attrGet('src'), title: e.attrGet('title') || null, alt: (e.children[0] && e.children[0].content) || null }) },
        hardbreak: { node: 'hard_break' },
        em: { mark: 'em' },
        strong: { mark: 'strong' },
        link: { mark: 'link', getAttrs: (e: any) => ({ href: e.attrGet('href'), title: e.attrGet('title') || null }) },
        code_inline: { mark: 'code' },
        // Table tokens (thead/tbody are structural in markdown-it but don't map to PM nodes)
        table: { block: 'table' },
        thead: { ignore: true },
        tbody: { ignore: true },
        tr: { block: 'table_row' },
        th: { block: 'table_header' },
        td: { block: 'table_cell' },
    });

    // Extend the default serializer with table node serializers
    private mdSerializer = new MarkdownSerializer(
        {
            ...defaultMarkdownSerializer.nodes,
            table: (state, node) => {
                node.forEach((row, _, rowIndex) => {
                    const cellTexts: string[] = [];
                    row.forEach(cell => {
                        cellTexts.push(cell.textContent.replace(/\|/g, '\\|').replace(/\n/g, ' '));
                    });
                    state.write('| ' + cellTexts.join(' | ') + ' |');
                    state.ensureNewLine();
                    // Write separator after the first (header) row
                    if (rowIndex === 0) {
                        state.write('| ' + cellTexts.map(() => '---').join(' | ') + ' |');
                        state.ensureNewLine();
                    }
                });
                state.closeBlock(node);
            },
            table_row: () => { /* handled by table serializer */ },
            table_header: () => { /* handled by table serializer */ },
            table_cell: () => { /* handled by table serializer */ },
        },
        defaultMarkdownSerializer.marks
    );

    private currentContent = '';
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly DEBOUNCE_DELAY = 1000;

    showToolbar = false;
    toolbarPosition = { top: 0, left: 0 };
    selectedText = '';
    showHeadingsPanel = false;
    headingsPanelAbove = false; // Whether headings panel should appear above toolbar
    showInput = false;
    inputValue = '';

    MessageCirclePlus = MessageCirclePlus
    ArrowUp = ArrowUp
    GENERAL_AGENT_SVG = GENERAL_AGENT_SVG

    // ───────────────────────────────── constructors / effects ─────────────────────────────────

    constructor(
        private generalAgentService: GeneralAgentService,
        private threadMessagesService: ThreadMessagesService,
        private cdr: ChangeDetectorRef,
        private toastService: ToastService
    ) {
        effect(() => {
            const newContent = this.canvasContent();
            if (this.editorView && newContent !== this.currentContent) {
                this.updateEditorContent(newContent);
            }
        });

        // if isEditable becomes false, hide toolbar
        effect(() => {
            if (!this.isEditable()) {
                this.showToolbar = false;
                this.cdr.detectChanges();
            }
        });
    }

    // ───────────────────────────── placeholder plugin ────────────────────────────

    private placeholderPlugin = new Plugin({
        props: {
            decorations(state) {
                const { doc, selection } = state;
                const { empty, $from } = selection;
                if (!empty) return null;

                const parent = $from.parent;
                if (!parent.isTextblock || parent.content.size > 0) return null;

                const pos = $from.pos;
                const deco = Decoration.widget(
                    pos,
                    () => {
                        const span = document.createElement('span');
                        span.className = 'pm-placeholder';
                        span.textContent = 'Write something...';
                        return span;
                    },
                    { side: 1 }
                );

                return DecorationSet.create(doc, [deco]);
            },
        },
    });

    // ───────────────────────────── selection toolbar plugin  ──────────────────────────

    /** Plugin that reacts to selection changes and tells Angular to show/move the toolbar */
    private toolbarPlugin = new Plugin({
        view: (view) => {
            let lastState: EditorState | null = null;
            let isMouseDown = false;

            const handleMouseDown = (e: MouseEvent) => {
                if (e.button !== 0) return; // only left click
                isMouseDown = true;

                // hide toolbar while user is dragging
                if (this.showToolbar) {
                    this.showToolbar = false;
                    this.cdr.detectChanges();
                }
            };

            const handleMouseUp = () => {
                if (!isMouseDown) return;
                isMouseDown = false;

                // after mouse up, update once with final selection
                // small timeout to let ProseMirror finalize selection
                setTimeout(() => {
                    this.updateToolbarPosition();
                }, 0);
            };

            // attach listeners
            const dom = view.dom;
            dom.addEventListener('mousedown', handleMouseDown);
            window.addEventListener('mouseup', handleMouseUp);

            // initial sync (e.g. programmatic selection)
            this.updateToolbarPosition();

            return {
                update: (v, prevState) => {
                    const state = v.state;

                    // skip if nothing changed
                    if (
                        prevState &&
                        prevState.doc.eq(state.doc) &&
                        prevState.selection.eq(state.selection)
                    ) {
                        return;
                    }

                    // while mouse is down (dragging selection), do NOT show toolbar
                    if (isMouseDown) return;

                    // keyboard selection / programmatic changes still update normally
                    this.updateToolbarPosition();
                },
                destroy: () => {
                    dom.removeEventListener('mousedown', handleMouseDown);
                    window.removeEventListener('mouseup', handleMouseUp);
                    this.showToolbar = false;
                    this.cdr.detectChanges();
                },
            };
        },
    });

    private selectionHighlightPluginKey = new PluginKey<DecorationSet>('selectionHighlight');

    private selectionHighlightPlugin = new Plugin<DecorationSet>({
        key: this.selectionHighlightPluginKey,
        state: {
            init: () => {
                return DecorationSet.empty;
            },
            apply: (tr, oldDecos) => {
                // arrow fn ⇒ `this` is the CanvasComponent instance
                let decos = oldDecos.map(tr.mapping, tr.doc);

                const meta = tr.getMeta(this.selectionHighlightPluginKey);

                if (meta?.type === 'set') {
                    const { from, to } = meta;
                    if (typeof from === 'number' && typeof to === 'number' && from !== to) {
                        const deco = Decoration.inline(from, to, {
                            class: 'pm-frozen-selection',
                        });
                        decos = DecorationSet.create(tr.doc, [deco]);
                    }
                } else if (meta?.type === 'clear') {
                    decos = DecorationSet.empty;
                }

                return decos;
            },
        },
        props: {
            decorations: (state) => {
                // use the key to retrieve plugin state
                return this.selectionHighlightPluginKey.getState(state) ?? null;
            },
        },
    });


    // Helper method to update toolbar position based on selection
    private updateToolbarPosition(forceUpdate = false): void {

        if (!this.editorView || !this.editorContainer || !this.isEditable()) {
            this.showToolbar = false;
            this.cdr.detectChanges();
            return;
        }

        const { state } = this.editorView;
        const { selection } = state;
        const { empty, from, to } = selection;

        if (empty) {
            this.showToolbar = false;
            this.cdr.detectChanges();
            return;
        }

        // Get selected text
        const selectedText = state.doc.textBetween(from, to, ' ');

        if (selectedText.trim().length > 0) {
            // Get coordinates for the selection (relative to viewport)
            const start = this.editorView.coordsAtPos(from);
            const end = this.editorView.coordsAtPos(to);

            // Get parent container position relative to viewport (toolbar is positioned relative to this)
            const parentRect = this.parentContainer?.nativeElement?.getBoundingClientRect() ||
                this.editorContainer.nativeElement.getBoundingClientRect();

            // Get editor container for boundary calculations
            const editorRect = this.editorContainer.nativeElement.getBoundingClientRect();

            // Calculate initial position relative to parent container
            let toolbarTop = start.top - parentRect.top - 50; // 50px above selection
            let toolbarLeft = (start.left + end.left) / 2 - parentRect.left; // Center horizontally

            // Get toolbar dimensions (use a default if not yet rendered)
            const TOOLBAR_HEIGHT = 44; // Approximate height in pixels
            const TOOLBAR_WIDTH = 280; // Approximate width in pixels

            // Get actual toolbar dimensions if available
            let toolbarWidth = TOOLBAR_WIDTH;
            let toolbarHeight = TOOLBAR_HEIGHT;

            if (this.toolbarElement?.nativeElement) {
                const toolbarRect = this.toolbarElement.nativeElement.getBoundingClientRect();
                toolbarWidth = toolbarRect.width;
                toolbarHeight = toolbarRect.height;
            }

            // Get parent container dimensions for boundary checks
            const parentWidth = parentRect.width;
            const parentHeight = parentRect.height;

            // Get editor container dimensions for visible area
            const editorWidth = editorRect.width;
            const editorHeight = editorRect.height;
            const editorTop = editorRect.top - parentRect.top; // Editor position relative to parent
            const editorLeft = editorRect.left - parentRect.left;

            // Boundary checks with padding
            const PADDING = 10; // Padding from edges

            // Check horizontal boundaries (toolbar is centered with translateX(-50%))
            const halfToolbarWidth = toolbarWidth / 2;
            const minLeft = halfToolbarWidth + PADDING;
            const maxLeft = parentWidth - halfToolbarWidth - PADDING;

            // Also check against editor container boundaries
            const editorMinLeft = editorLeft + halfToolbarWidth + PADDING;
            const editorMaxLeft = editorLeft + editorWidth - halfToolbarWidth - PADDING;

            const effectiveMinLeft = Math.max(minLeft, editorMinLeft);
            const effectiveMaxLeft = Math.min(maxLeft, editorMaxLeft);

            if (toolbarLeft < effectiveMinLeft) {
                // Too far left, adjust to minimum position
                toolbarLeft = effectiveMinLeft;
            } else if (toolbarLeft > effectiveMaxLeft) {
                // Too far right, adjust to maximum position
                toolbarLeft = effectiveMaxLeft;
            }

            // Check vertical boundaries
            const minTop = PADDING;
            const maxTop = parentHeight - toolbarHeight - PADDING;

            // Also check against editor container boundaries
            const editorMinTop = editorTop + PADDING;
            const editorMaxTop = editorTop + editorHeight - toolbarHeight - PADDING;

            const effectiveMinTop = Math.max(minTop, editorMinTop);
            const effectiveMaxTop = Math.min(maxTop, editorMaxTop);

            if (toolbarTop < effectiveMinTop) {
                // Too close to top, try positioning below selection instead
                const belowTop = end.bottom - parentRect.top + 10; // 10px below selection

                if (belowTop + toolbarHeight <= effectiveMaxTop + toolbarHeight) {
                    // Fits below, use that position
                    toolbarTop = belowTop;
                } else {
                    // Doesn't fit below either, position at top with padding
                    toolbarTop = effectiveMinTop;
                }
            } else if (toolbarTop + toolbarHeight > effectiveMaxTop + toolbarHeight) {
                // Too close to bottom, try positioning above selection
                const aboveTop = start.top - parentRect.top - toolbarHeight - 10;

                if (aboveTop >= effectiveMinTop) {
                    // Fits above, use that position
                    toolbarTop = aboveTop;
                } else {
                    // Doesn't fit above either, position at bottom with padding
                    toolbarTop = Math.max(effectiveMinTop, effectiveMaxTop);
                }
            }

            // Final boundary check to ensure toolbar stays within parent bounds
            toolbarTop = Math.max(effectiveMinTop, Math.min(toolbarTop, effectiveMaxTop));
            toolbarLeft = Math.max(effectiveMinLeft, Math.min(toolbarLeft, effectiveMaxLeft));

            // Update toolbar position
            this.toolbarPosition = {
                top: toolbarTop,
                left: toolbarLeft,
            };
            this.selectedText = selectedText;
            this.showToolbar = true;

            // Recalculate headings panel position if it's open
            if (this.showHeadingsPanel) {
                setTimeout(() => {
                    this.calculateHeadingsPanelPosition();
                }, 0);
            }

            // Use setTimeout to ensure DOM is updated before recalculating if needed
            setTimeout(() => {
                this.cdr.detectChanges();
                // Recalculate with actual dimensions if toolbar is now rendered
                if (this.toolbarElement?.nativeElement && this.showToolbar) {
                    const actualToolbarRect = this.toolbarElement.nativeElement.getBoundingClientRect();
                    const actualWidth = actualToolbarRect.width;
                    const actualHeight = actualToolbarRect.height;

                    // Only recalculate if dimensions are significantly different
                    if (Math.abs(actualWidth - toolbarWidth) > 10 || Math.abs(actualHeight - toolbarHeight) > 10) {
                        this.updateToolbarPosition(true);
                    }
                }
            }, 0);
        } else {
            this.showToolbar = false;
            this.cdr.detectChanges();
        }
    }

    // ───────────────────────────── ngAfterViewInit / editor setup ────────────────────────────

    ngAfterViewInit(): void {
        if (!this.editorContainer) return;

        const contentValue = this.canvasContent();
        this.currentContent = contentValue || '';

        const doc = this.currentContent.trim()
            ? this.mdParser.parse(this.currentContent)
            : this.mySchema.node('doc', null, this.mySchema.node('paragraph'));

        const state = EditorState.create({
            schema: this.mySchema,
            doc,
            plugins: [
                history(),
                tableEditing(),
                this.placeholderPlugin,
                this.toolbarPlugin,
                this.selectionHighlightPlugin,
                keymap({
                    //Move across cells
                    'Tab': goToNextCell(1),
                    'Shift-Tab': goToNextCell(-1),
                    //Add a new row
                    'Enter': (state, dispatch, view) => {
                        if (!isInTable(state)) return false;
                        const { $from } = state.selection;
                        const row = $from.node($from.depth - 1);
                        // Only add a row when cursor is in the last cell of the row
                        const cellIndex = $from.index($from.depth - 1);
                        if (cellIndex < row.childCount - 1) return false;
                        // Add a new row after and jump to its first cell
                        if (!addRowAfter(state, dispatch)) return false;
                        if (view) goToNextCell(1)(view.state, view.dispatch);
                        return true;
                    },
                    //Delete a row
                    'Backspace': (state, dispatch) => {
                        const { $from, empty } = state.selection;
                        if (!empty) return false;
                        // With inline* cells, $from.parent IS the cell
                        const cell = $from.parent;
                        const isInCell = cell?.type.name === 'table_cell' || cell?.type.name === 'table_header';
                        if (!isInCell || cell.textContent !== '' || $from.parentOffset !== 0) return false;
                        // row is one level up from cell
                        const row = $from.node($from.depth - 1);
                        const table = $from.node($from.depth - 2);
                        if (!table || table.childCount <= 1) return false;
                        // Only delete if every cell in the row is empty
                        let rowIsEmpty = true;
                        row?.forEach(c => { if (c.textContent !== '') rowIsEmpty = false; });
                        if (!rowIsEmpty) return false;
                        return deleteRow(state, dispatch);
                    },
                    'Mod-z': undo,
                    'Mod-y': redo,
                    'Mod-Shift-z': redo,
                    'Mod-b': this.mySchema.marks['strong']
                        ? toggleMark(this.mySchema.marks['strong'])
                        : () => false,
                    'Mod-i': this.mySchema.marks['em']
                        ? toggleMark(this.mySchema.marks['em'])
                        : () => false,
                }),
                keymap(baseKeymap),
            ],
        });

        this.editorView = new EditorView(this.editorContainer.nativeElement, {
            state,
            editable: () => this.isEditable(),
            dispatchTransaction: (transaction) => {
                if (!this.editorView) return;

                const oldState = this.editorView.state;

                const newState = oldState.apply(transaction);
                this.editorView.updateState(newState);

                if (!oldState.doc.eq(newState.doc) && this.isEditable()) {
                    this.currentContent = this.mdSerializer.serialize(newState.doc);

                    if (this.debounceTimer) {
                        clearTimeout(this.debounceTimer);
                    }

                    this.debounceTimer = setTimeout(() => {
                        this.generalAgentService.editCanvasManually(this.getCanvasId(), this.currentContent, this.message()?.id || '', this.threadMetadataId() || '')
                            .then((res) => {
                                if (res.success) {
                                    this.threadMessagesService.updateMessage(
                                        this.message()!.id,
                                        { additional_kwargs: { ...this.message()!.additional_kwargs, canvasContent: this.currentContent } }
                                    );
                                } else {
                                    this.toastService.error('Failed to edit canvas');
                                }
                            });
                        this.debounceTimer = null;
                    }, this.DEBOUNCE_DELAY);
                }
            },
        });

        // Remove outline from editor element
        const contentEditableElements =
            this.editorContainer.nativeElement.querySelectorAll(
                '[contenteditable="true"]'
            );
        contentEditableElements.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.outline = 'none';
            htmlEl.style.boxShadow = 'none';
        });
    }

    // ───────────────────────────── content updates / toolbar actions ─────────────────────────

    private updateEditorContent(newContent: string): void {
        if (!this.editorView || !newContent) return;
        try {
            const doc = this.mdParser.parse(newContent);
            const tr = this.editorView.state.tr.replaceWith(
                0,
                this.editorView.state.doc.content.size,
                doc.content
            );
            const newState = this.editorView.state.apply(tr);
            this.editorView.updateState(newState);
            this.currentContent = newContent;
        } catch (error) {
            console.error('Error parsing markdown content:', error);
        }
    }

    private freezeCurrentSelection(): void {
        if (!this.editorView) return;

        const { state } = this.editorView;
        const { from, to, empty } = state.selection;

        if (empty || from === to) return;

        // Freeze current selection as decoration
        let tr = state.tr.setMeta(this.selectionHighlightPluginKey, {
            type: 'set',
            from,
            to,
        });

        this.editorView.dispatch(tr);
    }

    onAskBob(): void {
        this.freezeCurrentSelection();

        this.showToolbar = false;
        this.showInput = true;
        this.inputValue = '';
        this.cdr.detectChanges();
        // Auto-resize after view is updated
        setTimeout(() => {
            this.autoResizeTextarea();
        }, 0);
    }

    autoResizeTextarea(): void {
        if (!this.bobInput?.inputElement) return;

        const textarea = this.bobInput.inputElement.nativeElement;
        if (textarea.tagName !== 'TEXTAREA') return;

        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        // Set height based on scrollHeight, with a min height of one line
        const minHeight = 32; // Minimum height in pixels (approximately one line)
        const newHeight = Math.max(minHeight, textarea.scrollHeight);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY = newHeight >= 200 ? 'auto' : 'hidden'; // Show scrollbar if too tall
    }

    clearFrozenSelectionHighlight(): void {
        if (this.editorView) {
            this.editorView.dispatch(
                this.editorView.state.tr.setMeta(
                    this.selectionHighlightPluginKey,
                    { type: 'clear' }
                )
            );
        }
    }

    onInputSubmit(): void {
        this.clearFrozenSelectionHighlight();
        if (this.inputValue.trim()) {
            this.editCanvasWithAi.emit({ query: this.inputValue.trim(), selectedCanvasText: this.selectedText.trim(), canvasName: this.message()!.additional_kwargs?.canvasName });
            this.threadMessagesService.updateMessage(
                this.message()!.id,
                { additional_kwargs: { ...this.message()!.additional_kwargs, isLatestVersion: false } }
            );
            this.showInput = false;
            this.inputValue = '';
            this.cdr.detectChanges();
        }
    }

    onInputCancel(): void {
        this.showInput = false;
        this.inputValue = '';
        this.cdr.detectChanges();
    }

    onToggleBold(): void {
        if (!this.editorView || !this.mySchema.marks['strong']) return;

        const { state, dispatch } = this.editorView;
        const markType = this.mySchema.marks['strong'];

        toggleMark(markType)(state, dispatch);
        this.editorView.focus();

        // keep toolbar positioned correctly after toggling
        setTimeout(() => {
            this.updateToolbarPosition();
        }, 0);
    }

    onToggleItalic(): void {
        if (!this.editorView || !this.mySchema.marks['em']) return;

        const { state, dispatch } = this.editorView;
        const markType = this.mySchema.marks['em'];

        toggleMark(markType)(state, dispatch);
        this.editorView.focus();

        setTimeout(() => {
            this.updateToolbarPosition();
        }, 0);
    }

    // ───────────────────────────── headings panel ─────────────────────────────────

    onToggleHeadingsPanel(event: Event): void {
        event.stopPropagation();
        const wasOpen = this.showHeadingsPanel;
        this.showHeadingsPanel = !this.showHeadingsPanel;

        if (this.showHeadingsPanel && !wasOpen) {
            // Calculate available space when opening the panel
            this.calculateHeadingsPanelPosition();
        }

        this.cdr.detectChanges();
    }

    private calculateHeadingsPanelPosition(): void {
        if (!this.toolbarElement?.nativeElement || !this.parentContainer?.nativeElement) {
            return;
        }

        const toolbarRect = this.toolbarElement.nativeElement.getBoundingClientRect();
        const parentRect = this.parentContainer.nativeElement.getBoundingClientRect();

        // Get toolbar position relative to parent
        const toolbarTop = toolbarRect.top - parentRect.top;
        const toolbarBottom = toolbarRect.bottom - parentRect.top;

        // Get available space above and below toolbar
        const spaceAbove = toolbarTop;
        const spaceBelow = parentRect.height - toolbarBottom;

        // Get headings panel height (use actual height if available, otherwise estimate)
        let panelHeight = 0;
        if (this.headingsPanel?.nativeElement) {
            const headingsPanelRect = this.headingsPanel.nativeElement.getBoundingClientRect();
            panelHeight = headingsPanelRect.height;
        }

        // If panel height is 0, estimate it (approximately 4 options * 40px each + padding)
        if (panelHeight === 0) {
            panelHeight = 200; // Estimate: 4 options * ~40px + padding
        }

        const gap = 8; // Gap between toolbar and panel (0.5rem = 8px)

        // Check if panel fits below
        const fitsBelow = spaceBelow >= panelHeight + gap;
        // Check if panel fits above
        const fitsAbove = spaceAbove >= panelHeight + gap;

        // Determine position: prefer below if it fits, otherwise above if it fits, otherwise choose side with more space
        if (fitsBelow) {
            this.headingsPanelAbove = false;
        } else if (fitsAbove) {
            this.headingsPanelAbove = true;
        } else {
            // Neither fits perfectly, choose the side with more space
            this.headingsPanelAbove = spaceAbove > spaceBelow;
        }

        // Recalculate after DOM update if panel wasn't rendered yet
        if (this.headingsPanel?.nativeElement) {
            const headingsPanelRect = this.headingsPanel.nativeElement.getBoundingClientRect();
            if (headingsPanelRect.height === 0) {
                setTimeout(() => {
                    this.calculateHeadingsPanelPosition();
                    this.cdr.detectChanges();
                }, 0);
            }
        }
    }

    onHeadingSelected(headingType: 'paragraph' | 'heading' | null, level?: number, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        if (!this.editorView) return;

        const { state, dispatch } = this.editorView;
        const { schema } = state;

        // Close the headings panel
        this.showHeadingsPanel = false;

        if (headingType === 'paragraph') {
            // Convert to paragraph
            const paragraphType = schema.nodes['paragraph'];
            if (paragraphType) {
                setBlockType(paragraphType)(state, dispatch);
            }
        } else if (headingType === 'heading' && level !== undefined) {
            // Convert to heading with specified level
            const headingType = schema.nodes['heading'];
            if (headingType) {
                setBlockType(headingType, { level })(state, dispatch);
            }
        }

        // Focus back on the editor
        this.editorView.focus();

        // Update toolbar position after block type change
        setTimeout(() => {
            this.updateToolbarPosition();
            this.cdr.detectChanges();
        }, 0);
    }

    // ───────────────────────────── window resize handler ─────────────────────────────────

    @HostListener('window:resize')
    onWindowResize(): void {
        if (this.showHeadingsPanel) {
            this.calculateHeadingsPanelPosition();
            this.cdr.detectChanges();
        }
    }

    // ───────────────────────────── document click handler ─────────────────────────────────

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;

        // Check if click is inside the headings panel
        const headingsPanelElement = this.headingsPanel?.nativeElement;
        const isInsideHeadingsPanel = headingsPanelElement && headingsPanelElement.contains(target);

        // Close headings panel if clicking outside
        if (this.showHeadingsPanel && !isInsideHeadingsPanel) {
            const headingsButton = target.closest('.headings-button-wrapper');
            if (!headingsButton) {
                this.showHeadingsPanel = false;
                this.clearFrozenSelectionHighlight();
                this.cdr.detectChanges();
            }
        }

        // ✅ Only close input if click is outside BOTH the input AND the toolbar / Ask Bob button
        if (this.showInput) {
            const inputContainer = target.closest('.ask-bob-input-container');
            const toolbarElement = target.closest('.floating-toolbar');
            const askBobButton = target.closest('.ask-bob-button');

            if (!inputContainer && !toolbarElement && !askBobButton) {
                this.clearFrozenSelectionHighlight();
                this.onInputCancel();
                return;
            }
        }

        if (!this.showToolbar) return;

        const parentElement = this.parentContainer?.nativeElement;
        const toolbarElement = this.toolbarElement?.nativeElement;

        if (
            parentElement && !parentElement.contains(target) &&
            toolbarElement && !toolbarElement.contains(target) &&
            !isInsideHeadingsPanel
        ) {
            this.showToolbar = false;
            this.clearFrozenSelectionHighlight();
            this.cdr.detectChanges();
        }
    }

    // Missing method
    getCanvasId() {
        const message = this.message();
        if (!message) return '';
        return message?.['response_metadata']?.attachmentId || '';
    }

    onKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.onInputSubmit();
        }
    }

    /**
     * Gets the rendered HTML content from the ProseMirror editor
     * ProseMirror renders semantic HTML (strong, em, h1-h6, p, etc.) which preserves formatting
     * @returns The HTML string of the rendered content
     */
    getRenderedHTML(): string {
        if (!this.editorView || !this.editorContainer) {
            return '';
        }

        // Get the ProseMirror content DOM element
        // The editorView.dom contains the rendered HTML with semantic tags
        const editorDOM = this.editorView.dom;
        const contentElement = editorDOM.querySelector('.ProseMirror') || editorDOM;

        // Clone to avoid modifying the original DOM
        const clonedElement = contentElement.cloneNode(true) as HTMLElement;

        // Return the innerHTML which contains the semantic HTML structure
        // Applications that support HTML paste will recognize tags like <strong>, <em>, <h1-h6>, etc.
        return clonedElement.innerHTML;
    }

    ngOnDestroy(): void {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        if (this.editorView) {
            this.editorView.destroy();
            this.editorView = null;
        }

        this.showToolbar = false;
    }
}
